import React, { useMemo, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom';
import { translate } from '../utils/translator';
import { useCartContext } from '../customHooks/useCart';
import CartViewProductRow from './CartViewProductRow';
import RiskGroupedProductList from './RiskGroupedProductList';
import { EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS, TOOLTIP_PHARMACY } from '../utils/consts';
import CartViewSubscriptionPlans from './CartViewSubscriptionPlans';
import UpsellUpgradeBanner from './UpsellUpgradeBanner';
import { useShippingPageContext } from '../pages/v2/ShippingV2/providers/ShippingPageProvider';
import CartViewSummaryDE from './CartViewSummaryDE';
import { ORDER_FLOWS } from '../utils/consts';
import VideoConsultationRow from './VideoConsultationRow';
import DisclaimerTooltip from './DisclaimerTooltip';
import CartViewBreakdown from './CartViewBreakdown';
import RxUpsellRegretBanner from './RxUpsellRegretBanner';
import { groupProductsByRiskGroup } from '../utils/groupProductsByRiskGroup';

type Props = {
  className?: string
  showProductsQuantityControl?: boolean
  hideSubscriptionPlans?: boolean
  trackFinalCartPrice?: boolean
  shippingFee?: number
  setRxProductFinalPrice?: (price: number) => void //TODO: Delete it when old shipping is dead
  isExpressShipping?: boolean
  isCannabisCatalog?: boolean
  OTCAddonsTrigger: React.ReactNode
  productsGroupedByPharmacy?: Array<{pharmacyName: string, productItemIDs: number[]}>
  onExpandProducts?: () => void
  onTooltipOpen?: (tooltipId: string) => void
  showDelayedCaptureDisclaimer?: boolean
  orderShippingType?: string
  summaryAfterProducts?: boolean
  hideCostDetails?: boolean
}

const CartViewDE: React.FC<Props> = ({ className, hideSubscriptionPlans = false, showProductsQuantityControl=true , setRxProductFinalPrice, trackFinalCartPrice=false, isExpressShipping=false ,isCannabisCatalog=false, OTCAddonsTrigger, productsGroupedByPharmacy=[], onExpandProducts, onTooltipOpen, showDelayedCaptureDisclaimer=false, orderShippingType, summaryAfterProducts=false, hideCostDetails=false}) => {

  const { language } = useParams()
  const { getCart } = useCartContext();
  const [showProducts, setShowProducts] = useState(true);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  

  const cart = getCart();
  const {
  setShowUpsellPopUp,
  eligibleForUpsell,
  wasUpgradedToPremium,
  showRxRegretBanner,
  setShowRxPremiumUpsellPopup,
  handleRxRegretDismiss,
  country,
} = useShippingPageContext();


const isSchengenFlow = cart.flow === ORDER_FLOWS.SCHENGEN;
  const isSelfPickup = useMemo(() => {
    return cart?.selected_service_option === "self_pickup";
  }, [cart?.selected_service_option]);
  
  const isOnlyPrescription = useMemo(() => {
    return cart?.selected_service_option === "only_prescription" || cart?.only_prescription;
  }, [cart?.selected_service_option]);

  const rxProducts = useMemo(() => {
    return groupProductsByRiskGroup(cart.products.filter((product) => product.is_prescription_drug))
  }, [cart.products]);


  const shouldShowDisclaimerAndBreakdown = useMemo(() => {
    return !cart.only_prescription && cart.flow !== ORDER_FLOWS.OTC_ONLY && !isCannabisCatalog
  }, [cart.only_prescription, cart.flow]);

  const rxProductsGroupedByPharmacy = useMemo(() => { 
    if(!isCannabisCatalog || !productsGroupedByPharmacy || productsGroupedByPharmacy.length === 0) {
      return null;
    }

    const groupedProducts: { [pharmacyName: string]: any[] } = {};

    productsGroupedByPharmacy.forEach((pharmacy) => {
      groupedProducts[pharmacy.pharmacyName] = [];
    })

    // Group prescription products by pharmacy
    rxProducts.forEach(product => {
      const pharmacy = productsGroupedByPharmacy.find(p => 
        p.productItemIDs.includes(product.product_item_id!)
      );
      if (pharmacy) {
        groupedProducts[pharmacy.pharmacyName].push(product);
      }
    });

    return groupedProducts;
  }, [rxProducts, productsGroupedByPharmacy, isCannabisCatalog])  

  const addonProducts = useMemo(() => {
    return groupProductsByRiskGroup(cart.products.filter((product) => !product.is_prescription_drug))
  }, [cart.products])

  // Auto-show products when OTC products are added
  useEffect(() => {
    if (addonProducts.length > 0) {
      setShowProducts(true);
    }
  }, [addonProducts.length]);

  const summary = (
    <div className="flex flex-col bg-white px-5 pb-2 pt-4 rounded-[16px] h-fit gap-2 w-full">
      <CartViewSummaryDE trackFinalCartPrice={trackFinalCartPrice} setRxProductFinalPrice={setRxProductFinalPrice} isExpressShipping={isExpressShipping} isCannabisCatalog={isCannabisCatalog} onTooltipOpen={onTooltipOpen} showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer} orderShippingType={orderShippingType} />
      {shouldShowDisclaimerAndBreakdown && (
        <div id='price-disclaimer' className='bg-primary-lightgreen-surface border-2 border-[#0D2C54] rounded-lg p-3 shadow-sm '>
          <p className='text-text-success font-roboto font-medium text-center text-xs'>
            {translate(language, "shipping", "CostBreakdown/price_disclaimer")}
          </p>
          <p className='text-[#656D79] font-roboto font-normal text-center text-xs'>
            {translate(language, "shipping", "CostBreakdown/price_disclaimer_description")}
          </p>
        </div>
      )}
      {hideSubscriptionPlans ? null : <CartViewSubscriptionPlans />}
      {OTCAddonsTrigger && OTCAddonsTrigger}
    </div>
  );

  return (
    <div className={`flex flex-col w-full h-fit ${className || ''}`}>
      {!summaryAfterProducts && summary}

      {!summaryAfterProducts && (
        <div className='w-full mb-3' style={{ borderTop: "1px dashed #0A9281" }} />
      )}

      {showRxRegretBanner && (
        <RxUpsellRegretBanner
          country={country}
          onUpgradeClick={() => setShowRxPremiumUpsellPopup(true)}
          onDismiss={handleRxRegretDismiss}
        />
      )}

      <div id='cart-view-container' className="flex flex-col bg-white px-5 pt-2 rounded-[16px] h-fit gap-6 relative overflow-visible lg:overflow-visible w-full">
        {/* Desktop version - hidden on mobile */}
        {eligibleForUpsell && !wasUpgradedToPremium && 
          <div className="hidden lg:block">
           <UpsellUpgradeBanner onUpgradeClick={() => setShowUpsellPopUp(true)} />
          </div>
        }


        <div id='cart-view-de-products-container' className='flex flex-col gap-2'>
          {/* Mobile: Products Toggle Button */}
          <div 
            onClick={() => {
              setShowProducts(!showProducts);
              onExpandProducts?.();
            }}
            className='flex justify-center items-center w-full cursor-pointer lg:hidden'
          >
            <span id ="mobile-products-toggle-button" className='text-[#269C6E] underline text-sm font-roboto hover:text-[#269C6E]/80 transition-colors'>
              {showProducts ? translate(language, "default", "OrderWindow/hide_products"): translate(language, "default", "OrderWindow/show_products")}
            </span>
          </div>

          {/* Mobile: Product Preview when hidden */}
          {!showProducts && !isSchengenFlow && (
            <div className='flex flex-col gap-2 pb-4 lg:hidden relative'>
              <h2 className='text-base font-roboto font-semibold text-[#0D2C54]'>{translate(language, "default", "OrderWindow/prescription_products")}</h2>
              <div className='absolute inset-0 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none'></div>
            </div>
          )}

          {/* Products Section - Shared Logic */}
          <div className={`${showProducts ? 'block' : 'hidden'} lg:block`}>
            {!isSchengenFlow && (
            <>
            <h2 className='text-base font-roboto font-semibold text-[#0D2C54]'>{translate(language, "default", "OrderWindow/prescription_products")}</h2>

            {/* Rx Product rows  */}
            <div>
            {
              rxProductsGroupedByPharmacy && isCannabisCatalog ? (
                Object.entries(rxProductsGroupedByPharmacy).map(([pharmacyName, products]) => products.length > 0 ? (
                  <div key={pharmacyName}>
                    <h3 className='text-sm font-roboto font-medium text-[#0D2C54] mt-4 mb-2 flex items-center gap-2 relative'>
                    {isSelfPickup || isOnlyPrescription ? "" : translate(language, "payment", "pharmacyPartnerText")} {pharmacyName}
                      {isSelfPickup || isOnlyPrescription ? null : 
                      <DisclaimerTooltip
                        tooltipText={translate(language, "payment", "pharmacyTooltip/tooltipText")}
                        isOpen={showTooltip === `${TOOLTIP_PHARMACY}_${pharmacyName}`}
                        onToggle={() => {
                          const newState = showTooltip === `${TOOLTIP_PHARMACY}_${pharmacyName}` ? null : `${TOOLTIP_PHARMACY}_${pharmacyName}`;
                          setShowTooltip(newState);
                          if (newState === `${TOOLTIP_PHARMACY}_${pharmacyName}`) {
                            onTooltipOpen?.(TOOLTIP_PHARMACY);
                          }
                        }}
                      />}
                    </h3>
                    {products.map((cartViewProduct) => (
                      <CartViewProductRow
                        key={cartViewProduct.product_item_id}
                        cartViewProduct={cartViewProduct}
                        className='py-3'
                        wasUpgradedToPremium={wasUpgradedToPremium}
                        isCannabisCatalog={isCannabisCatalog}
                      />
                    ))}
                  </div>
                ) : null)
              ) : (
                rxProducts.map((cartViewProduct) => (
                  <CartViewProductRow
                    key={cartViewProduct.product_item_id}
                    cartViewProduct={cartViewProduct}
                    className='py-3'
                    wasUpgradedToPremium={wasUpgradedToPremium}
                    isCannabisCatalog={isCannabisCatalog}
                  />
                ))
              )
            }
            </div>

            {/* Care+ Plan (HL-9781): intentionally NOT rendered here. The plan already appears in
                the top "Your Order" summary (CartViewSummaryDE) and in the "Cost details" breakdown
                (CartViewBreakdown), so showing it inside the "Prescription medications" product list
                duplicated it. */}
            </>
            )}


            {/* Addons products of the order (OTC, Cannabis card...)   */}
            {addonProducts.length > 0 && 
            <div style={{ borderBottom: "1px dashed #9D9D9D" }}>

              {!isSchengenFlow &&
              <h2 className='text-base font-roboto font-semibold text-[#0D2C54]'>{translate(language, "default", "OrderWindow/non_prescription_products")}</h2>
              }
              <RiskGroupedProductList
                products={addonProducts}
                showQuantityControl={(product) => showProductsQuantityControl && !["schengen_id", "cannabis_id", "video_consultation", "test_kit"].includes(product.type!)}
              />
            </div>
            }

            {/* Video consultation */}
            {cart.consultation_type === "video" && (
                <VideoConsultationRow />
            )}
          </div>
        </div>

        {/* Bottom fade overlay using inline CSS (independent of Tailwind) */}
        {!showProducts && (
          <div className='lg:hidden'
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '64px',
              background: 'linear-gradient(to top, rgba(255,255,255,0.98), rgba(255,255,255,0.88) 40%, rgba(255,255,255,0))',
              pointerEvents: 'none',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
              zIndex: 50
            }}
          />
        )}

      </div>

      {summaryAfterProducts && (
        <>
          <div className='w-full mb-3' style={{ borderTop: "1px dashed #0A9281" }} />
          {summary}
        </>
      )}

      {/* Images disclaimer */}
      {(!EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS.includes(cart.flow || "")) ?
        <p
          id='cart-view-images-disclaimer'
          className="text-xs text font-roboto text-center text-[#98a0af] mt-4 w-full">*{translate(language, "default", "OrderWindow/image_disclaimer")}</p>
        :
        null
      }
{isCannabisCatalog || hideCostDetails ? null : (
        <div className="w-full">
          <CartViewBreakdown isExpressShipping={isExpressShipping} />
        </div>
      )}
    </div>
  )
}

export default CartViewDE